import type { ComponentProps } from "react"
import React, {
	useCallback,
	useDeferredValue,
	useMemo,
	useRef,
	useState,
} from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import { format, parseISO } from "date-fns"
import { IconFlagFill18 as FlagFillIcon } from "nucleo-ui-fill-18"
import { IconFlagOutline18 as FlagIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import {
	Badge,
	Button,
	Checkbox,
	DateRangePicker,
	Dialog,
	Empty,
	IconButton,
	Loader,
	SegmentedControl,
	Tooltip,
} from "@productlane/ui"

import type {
	ChangelogSelection,
	PickerItem,
	PickerItemKind,
	PickerRange,
} from "./release-picker"
import { api } from "@/lib/api"
import {
	buildSelection,
	defaultSelectedKeys,
	doneLabel,
	newestFirst,
	rangeWindow,
	resolveLead,
	selectionSummary,
	toPickerItems,
} from "./release-picker"
import { KindIcon } from "./shipped-since-card"

type DateRange = ComponentProps<typeof DateRangePicker>["dateRange"]

interface Picks {
	items: PickerItem[]
	selected: Set<string>
	pinned: string | null
}

const GROUPS: { kind: PickerItemKind; label: string }[] = [
	{ kind: "project", label: "Projects" },
	{ kind: "issue", label: "Issues" },
	{ kind: "pullRequest", label: "Pull requests" },
]

const NO_ITEMS: PickerItem[] = []
const HEADER_HEIGHT = 44
const ROW_HEIGHT = 52

type Row =
	| { type: "header"; key: string; label: string; group: PickerItem[] }
	| { type: "item"; key: string; item: PickerItem }

function toRows(items: PickerItem[]): Row[] {
	return GROUPS.flatMap(({ kind, label }): Row[] => {
		const group = newestFirst(items.filter((item) => item.kind === kind))
		if (group.length === 0) return []
		return [
			{ type: "header", key: `header:${kind}`, label, group },
			...group.map((item): Row => ({ type: "item", key: item.key, item })),
		]
	})
}

interface ReleasePickerDialogProps {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	onGenerate: (selection: ChangelogSelection) => void
}

export const ReleasePickerDialog = React.memo(
	({ isOpen, onOpenChange, onGenerate }: ReleasePickerDialogProps) => {
		const [range, setRange] = useState<PickerRange>("lastRelease")
		const [customRange, setCustomRange] = useState<DateRange>(undefined)
		// Fixed per range, so the query key holds still while the dialog is open.
		const period = useMemo(
			() => rangeWindow(range, customRange),
			[range, customRange],
		)

		const candidates = api.linear.changelogCandidates.useQuery(
			{ since: period?.since ?? null, until: period?.until ?? null },
			{
				enabled: isOpen && !!period,
				staleTime: 60_000,
				keepPreviousData: true,
			},
		)
		const items = useMemo(
			() => (candidates.data ? toPickerItems(candidates.data) : []),
			[candidates.data],
		)

		// Picks belong to the list they were made on, so a new list (another
		// range) shows its defaults in the same render instead of a second one.
		const [picks, setPicks] = useState<Picks | null>(null)
		const defaults = useMemo(() => defaultSelectedKeys(items), [items])
		const current: Picks =
			picks?.items === items
				? picks
				: { items, selected: defaults, pinned: null }
		const selected = current.selected
		const pinnedLead = current.pinned

		const edit = useCallback(
			(change: (base: Picks) => Omit<Picks, "items">) => {
				setPicks((prev) => {
					const base: Picks =
						prev?.items === items
							? prev
							: { items, selected: defaults, pinned: null }
					return { items, ...change(base) }
				})
			},
			[items, defaults],
		)

		const lead = resolveLead(items, selected, pinnedLead)
		const summary = selectionSummary(items, selected)

		const lastReleaseAt = candidates.data?.lastReleaseAt
		const rangeOptions = useMemo(
			() =>
				[
					{
						value: "lastRelease",
						label: lastReleaseAt
							? `Since ${format(parseISO(lastReleaseAt), "MMM d")} release`
							: "Last 3 months",
					},
					{ value: "week", label: "Last 7 days" },
					{ value: "custom", label: "Custom range" },
				] as const,
			[lastReleaseAt],
		)

		const toggle = useCallback(
			(key: string) =>
				edit(({ selected, pinned }) => {
					const next = new Set(selected)
					if (next.has(key)) next.delete(key)
					else next.add(key)
					return { selected: next, pinned }
				}),
			[edit],
		)

		const toggleGroup = (group: PickerItem[], on: boolean) =>
			edit(({ selected, pinned }) => {
				const next = new Set(selected)
				for (const item of group) {
					if (on) next.add(item.key)
					else next.delete(item.key)
				}
				return { selected: next, pinned }
			})

		const makeMain = useCallback(
			(key: string) =>
				edit(({ selected }) => ({
					selected: new Set(selected).add(key),
					pinned: key,
				})),
			[edit],
		)

		const write = () => {
			if (!candidates.data || summary.total === 0) return
			onGenerate(
				buildSelection({ since: candidates.data.since, items, selected, lead }),
			)
		}

		const isLoading =
			candidates.isLoading || (candidates.isFetching && !items.length)

		return (
			<Dialog
				size="lg"
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				title="What was released"
				description="Tick what goes into this changelog, and flag the main topic it opens with."
				className="overflow-visible px-4"
				footerClassName="sm:justify-between"
				header={
					<div className="flex flex-wrap items-center gap-2 pt-2.5">
						<SegmentedControl
							name="release-range"
							value={range}
							options={rangeOptions}
							onValueChange={setRange}
							showLabels
						/>
						{range === "custom" && (
							<DateRangePicker
								dateRange={customRange}
								setDateRange={setCustomRange}
								className="h-7"
							/>
						)}
					</div>
				}
				footer={
					<>
						<div className="min-w-0 text-sm">
							<p>
								<span className="font-medium">{summary.total} selected</span>
								{summary.parts.length > 0 && (
									<span className="text-secondary">
										{" "}
										· {summary.parts.join(", ")}
									</span>
								)}
							</p>
							<p className="truncate text-xs text-secondary">
								Main topic: {lead ? lead.title : "tick at least one item"}
							</p>
						</div>
						<div className="flex w-full flex-col-reverse items-center gap-2 sm:w-auto sm:flex-row">
							<Button
								variant="ghost"
								onClick={() => onOpenChange(false)}
								className="w-full sm:w-auto"
							>
								Cancel
							</Button>
							<Button
								disabled={summary.total === 0 || !candidates.data}
								onClick={write}
								className="w-full sm:w-auto"
							>
								Write changelog
							</Button>
						</div>
					</>
				}
			>
				{!period ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center">
						<Empty
							title="Pick a date range"
							text="Choose the first and last day of this release."
							className="max-w-[20rem]"
						/>
					</div>
				) : candidates.error ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center">
						<Empty
							title="Couldn't load what was released"
							text={candidates.error.message}
							action={
								<Button
									variant="outline"
									size="sm"
									onClick={() => void candidates.refetch()}
								>
									Try again
								</Button>
							}
							className="max-w-[22rem]"
						/>
					</div>
				) : isLoading ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center gap-2">
						<Loader />
						<p className="ml-2">Reading what was released...</p>
					</div>
				) : items.length === 0 ? (
					<div className="flex h-[calc(100dvh-25rem)] items-center justify-center">
						<Empty
							title="Nothing was released in this range"
							text="Try a longer range."
							className="max-w-[20rem]"
						/>
					</div>
				) : (
					<PickerList
						items={items}
						selected={selected}
						mainKey={lead?.key ?? null}
						onToggle={toggle}
						onToggleGroup={toggleGroup}
						onMakeMain={makeMain}
					/>
				)}
			</Dialog>
		)
	},
)

// Its own component, so the virtualizer mounts with the scroll element inside
// the dialog's portal; owned by the dialog, it never saw the element appear.
function PickerList({
	items,
	selected,
	mainKey,
	onToggle,
	onToggleGroup,
	onMakeMain,
}: {
	items: PickerItem[]
	selected: Set<string>
	mainKey: string | null
	onToggle: (key: string) => void
	onToggleGroup: (group: PickerItem[], on: boolean) => void
	onMakeMain: (key: string) => void
}) {
	// The dialog paints first and the rows follow in a background render, so
	// the click answers at once; they land during the open animation.
	const deferredItems = useDeferredValue(items, NO_ITEMS)
	// Flat, so only the rows on screen render: a release can carry 150 items.
	const rows = useMemo(() => toRows(deferredItems), [deferredItems])
	const scrollRef = useRef<HTMLDivElement>(null)
	const virtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: (index) =>
			rows[index]?.type === "header" ? HEADER_HEIGHT : ROW_HEIGHT,
		getItemKey: (index) => rows[index]?.key ?? index,
		overscan: 4,
		initialRect: { width: 720, height: 640 },
		useFlushSync: false,
	})
	const now = new Date()

	return (
		<div
			ref={scrollRef}
			className="-ml-2 h-[calc(100dvh-25rem)] overflow-y-auto pb-2 pr-2"
		>
			<ul
				className="relative"
				style={{ height: `${virtualizer.getTotalSize()}px` }}
			>
				{virtualizer.getVirtualItems().map((virtual) => {
					const row = rows[virtual.index]
					if (!row) return null
					const position = {
						height: `${virtual.size}px`,
						transform: `translateY(${virtual.start}px)`,
					}
					if (row.type === "header") {
						const allOn = row.group.every((item) => selected.has(item.key))
						return (
							<li
								key={row.key}
								className="absolute left-0 top-0 flex w-full items-end justify-between px-3 pb-1"
								style={position}
							>
								<p className="text-xs font-medium text-secondary">
									{row.label} · {row.group.length}
								</p>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => onToggleGroup(row.group, !allOn)}
								>
									{allOn ? "Clear" : "Select all"}
								</Button>
							</li>
						)
					}
					return (
						<PickerRow
							key={row.key}
							item={row.item}
							style={position}
							done={doneLabel(row.item, now)}
							checked={selected.has(row.item.key)}
							isMain={mainKey === row.item.key}
							onToggle={onToggle}
							onMakeMain={onMakeMain}
						/>
					)
				})}
			</ul>
		</div>
	)
}

const PickerRow = React.memo(
	({
		item,
		done,
		checked,
		isMain,
		onToggle,
		onMakeMain,
		style,
	}: {
		item: PickerItem
		style: React.CSSProperties
		done: string | null
		checked: boolean
		isMain: boolean
		onToggle: (key: string) => void
		onMakeMain: (key: string) => void
	}) => {
		// A tooltip per row cost a third of the dialog's first render, so one
		// mounts only once the pointer reaches its row.
		const [hovered, setHovered] = useState(false)
		const meta = [
			item.reference,
			done,
			item.internal ? "Looks internal, so it starts unticked" : null,
		]
			.filter(Boolean)
			.join(" · ")
		const flag = (
			<IconButton
				variant="ghost"
				Icon={isMain ? FlagFillIcon : FlagIcon}
				iconClassName={cn("transform-gpu", {
					"text-amber-500": isMain,
					"text-tertiary": !isMain,
				})}
				aria-label={isMain ? "Main topic" : "Make this the main topic"}
				aria-pressed={isMain}
				onClick={() => onMakeMain(item.key)}
			/>
		)

		return (
			<li
				className="absolute left-0 top-0 flex w-full items-center gap-3 rounded-lg px-3 text-sm hover:bg-gray-100 dark:hover:bg-gray-850"
				style={style}
				onPointerEnter={hovered ? undefined : () => setHovered(true)}
			>
				<label className="flex min-w-0 flex-1 cursor-default items-center gap-3">
					<Checkbox
						checked={checked}
						onCheckedChange={() => onToggle(item.key)}
					/>
					<KindIcon kind={item.kind} />
					<span className="min-w-0 flex-1">
						<span
							className={cn("block truncate font-medium", {
								"text-secondary": !checked,
							})}
						>
							{item.title}
						</span>
						{meta && (
							<span className="block truncate text-xs text-secondary">
								{meta}
							</span>
						)}
					</span>
				</label>
				<span className="hidden flex-none items-center gap-1.5 sm:flex">
					{item.labels.slice(0, 2).map((label) => (
						<Badge key={label.name} variant="default">
							<span
								className="mr-1 inline-block h-1.5 w-1.5 flex-none rounded-full"
								style={{ backgroundColor: label.color ?? undefined }}
							/>
							{label.name}
						</Badge>
					))}
					{item.requestCount > 0 && (
						<Badge variant="default">
							{item.requestCount}{" "}
							{item.requestCount === 1 ? "request" : "requests"}
						</Badge>
					)}
					{isMain && <Badge variant="amber">Main topic</Badge>}
				</span>
				{hovered ? (
					<Tooltip
						label={
							isMain
								? "The changelog opens with this"
								: "Make this the main topic"
						}
					>
						{flag}
					</Tooltip>
				) : (
					flag
				)}
			</li>
		)
	},
)
